import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import useAxios from "axios-hooks";
import { useParams } from "react-router-dom";
import { ClassroomSelect } from "../../components/input/ClassroomSelect";

type FormValues = {
  classroomIds: string[];
  csv: FileList;
  name: string;
};

export default function ImportJobForm(props: {
  onSuccess: any;
  defaultValues?: any;
}) {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();

  const params = useParams();

  const [
    {
      data: createMemberData,
      loading: createMemberLoading,
      error: createMemberError,
    },
    createMember,
  ] = useAxios<any, FormData>(
    {
      url: "/import-jobs",
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    },
    { manual: true }
  );

  const [{ data: searchData }, getClassrooms] = useAxios(
    { url: "/classrooms" },
    { manual: true }
  );

  const [selectedClassroomOptions, setSelectedClassroomOptions] = useState(
    [] as any
  );

  const onSubmit = async (data: FormValues) => {
    const formData = new FormData();
    formData.append("file", data.csv[0]);
    formData.append("name", data.name);
    data.classroomIds.forEach((id) => formData.append("classroomIds[]", id));

    try {
      const response = await createMember({ data: formData });
      if (response.status === 201) {
        props.onSuccess(response.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      <label className="form-control w-full">
        <div className="label">
          <span className="label-text">Name</span>
        </div>
        <input
          {...register("name", { required: true })}
          className="input input-bordered input-sm"
        />
        {errors.name && (
          <span className="text-error text-sm">Name is required</span>
        )}
      </label>

      {/* Classroom select */}
      <label className="form-control w-full">
        <div className="label">
          <span className="label-text">Classrooms</span>
        </div>
        <Controller
          name="classroomIds"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <ClassroomSelect
              defaultSelectedOptions={props?.defaultValues?.classrooms?.map(
                (classroom: any) => {
                  return {
                    _id: classroom._id,
                    value: classroom._id,
                    label: classroom.name,
                  };
                }
              )}
              value={selectedClassroomOptions}
              onChange={(options: any) => {
                setSelectedClassroomOptions(selectedClassroomOptions);
                field.onChange(options);
              }}
              placeholder="Choose a classroom..."
            />
          )}
        />
        {errors.classroomIds && (
          <span className="text-error text-sm">Please select classrooms</span>
        )}
      </label>

      {/* CSV File Upload */}
      <label className="form-control w-full">
        <div className="label">
          <span className="label-text">CSV File</span>
        </div>
        <input
          type="file"
          accept=".csv"
          {...register("csv", { required: true })}
          className="file-input file-input-bordered file-input-sm w-full"
        />
        {errors.csv && (
          <span className="text-error text-sm">CSV file is required</span>
        )}
      </label>

      {/* Submit Button */}
      <button
        className="btn btn-sm btn-primary w-1/2 ml-auto mr-auto mt-10"
        type="submit"
      >
        START IMPORT JOB
      </button>

      {/* Error & Loading UI */}
      {createMemberError && (
        <div className=" text-sm mt-4 text-center  p-4 rounded border border-red-600 bg-red-50 text-red-900">
          <p className="font-semibold mb-2">
            {createMemberError?.response?.data?.message || "An error occurred"}
          </p>

          {/* Check if errors array exists */}
          {createMemberError?.response?.data?.errors?.length > 0 && (
            <div className="text-left">
              {createMemberError?.response?.data?.errors?.map(
                (
                  { row, errors, data }: { row: any; errors: any; data: any },
                  i: any
                ) => (
                  <div key={i} className="mb-4 border-b border-red-300 pb-2">
                    <p className="font-medium text-red-700 mb-1">
                      Row #{row} errors:
                    </p>

                    <ul className="list-disc list-inside mb-1 text-sm">
                      {errors.map(
                        (
                          {
                            property,
                            constraints,
                          }: { property: any; constraints: any },
                          j: any
                        ) => (
                          <li key={j}>
                            <strong>{property}:</strong>{" "}
                            {Object.values(constraints).join(", ")}
                          </li>
                        )
                      )}
                    </ul>

                    <pre className="bg-red-100 p-2 rounded text-xs overflow-x-auto">
                      {JSON.stringify(data, null, 2)}
                    </pre>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
      {createMemberLoading && (
        <div className="flex justify-center mt-4">
          <div className="loading loading-spinner" />
        </div>
      )}
    </form>
  );
}
